import type { Store } from '../persistence/port';
import { TABLES } from '../persistence/schema';
import { ARCHIV_WURZEL } from './archive';
import { appendAppLog } from './appLog';
import { liesBase64, liesGepackt, listeOrdner, type Gepackt } from './dateien';
import { ausBase64 } from './webcrypto';
import { ZipBauer } from './zipSchreiber';

/**
 * Die Belege aus dem Browser herausholen.
 *
 * ⚠️ **Warum es das gibt.** Am iPad liegen Aufnahmen, Bogenabzüge und Beglaubigungen in
 * Documents und gehen über Finder oder iTunes hinaus — seit dem 2026-09-11 ausdrücklich
 * freigegeben (`UIFileSharingEnabled`, s. `platform/storage.ts`). Im Browser liegt derselbe
 * Bestand in IndexedDB: **unsichtbar und nicht kopierbar**. Wer im Browser bewertet, käme an
 * seine eigenen Belege nicht heran. Das war die letzte Lücke, die wirklich wog.
 *
 * ⚠️ **Nur im Browser anbieten.** Am Gerät gibt es den Finder-Weg; ein zweiter Weg daneben
 * wäre eine zweite Wahrheit über denselben Bestand — und die kleinere von beiden fiele am
 * längsten nicht auf.
 */

/** Was mitkommt: der laufende Bestand und das Archiv gelöschter Prüfungen. */
export const WURZELN = ['logs', ARCHIV_WURZEL] as const;

/**
 * Die Geräte-Logs in der Wurzel von `Documents` (`ios/App/App/GeraetelogPlugin.swift`):
 * `log.txt`, `log.1.gz` … und dasselbe für `log-sql`. Sie kommen in **jedes** Archiv.
 */
export const IST_LOGDATEI = /^log(-sql)?(\.txt|\.\d+\.gz)$/;

/** Text lohnt das Packen (ein Log schrumpft auf etwa ein Zehntel); Bilder, Ton, `.gz` nicht. */
export const IST_PACKBAR = /\.(txt|log|json|svg|csv|xml)$/i;

export interface UebergabeTeile {
  liesOrdner: (pfad: string) => Promise<{ name: string; istOrdner: boolean }[]>;
  liesDatei: (pfad: string) => Promise<string>;
  /** Gepackt lesen; `null` heisst „hier nicht möglich", dann geht die Datei ungepackt hinein. */
  liesGepackt?: (pfad: string) => Promise<Gepackt | null>;
  jetzt: () => Date;
  /**
   * Das Dateilog. `stufe` bestimmt, unter welcher Konsolenfunktion die Zeile am Kabel in
   * Xcode erscheint (s. `platform/appLog.ts`); in der Datei sieht jede Zeile gleich aus.
   */
  log: (text: string, stufe?: 'warn') => Promise<void>;
}

export interface Uebergabe {
  stuecke: Uint8Array[];
  dateiname: string;
  dateien: number;
  bytes: number;
}

/** `belege-2026-09-22-1432.zip` — Datum und Uhrzeit, damit zwei Ausleitungen sich nicht decken. */
export function uebergabeName(d: Date): string {
  const p = (n: number, l = 2) => String(n).padStart(l, '0');
  return (
    'belege-' + d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) +
    '-' + p(d.getHours()) + p(d.getMinutes()) + '.zip'
  );
}

/**
 * Sammelt rekursiv ein und packt.
 *
 * ⚠️ **Eine Datei nach der anderen.** Alles zuerst einzulesen und dann zu packen hiesse, den
 * ganzen Bestand gleichzeitig im Speicher zu halten — bei einem Prüfungstag mit Videos sind
 * das schnell hunderte Megabyte. So steht immer nur eine Datei im Speicher, und die fertigen
 * Stücke wandern in ein `Blob`, das der Browser bei Bedarf auf die Platte legt.
 *
 * ⚠️ **Eine unlesbare Datei kostet nicht die Übergabe.** Sie fehlt im Archiv, und der Grund
 * steht im Dateilog — das ist besser, als an einer kaputten Datei die übrigen zu verlieren.
 */
export async function sammleBelege(
  teile: UebergabeTeile,
  optionen: {
    /** Zusätzliche Einträge vorneweg — etwa der Datenbestand im Diagnose-ZIP. */
    zusatz?: { name: string; daten: Uint8Array }[];
    /** Was nicht ins Archiv soll; es wird im Dateilog nicht erwähnt. */
    auslassen?: (pfad: string) => boolean;
  } = {},
): Promise<Uebergabe> {
  const bauer = new ZipBauer();
  const zeit = teile.jetzt();
  let dateien = 0;
  for (const z of optionen.zusatz ?? []) {
    bauer.fuegeHinzu(z.name, z.daten, zeit);
    dateien += 1;
  }

  const nimm = async (voll: string): Promise<void> => {
    if (teile.liesGepackt && IST_PACKBAR.test(voll)) {
      // Scheitert das Packen, versucht es unten der ungepackte Weg — der meldet dann den Grund.
      const gepackt = await teile.liesGepackt(voll).catch(() => null);
      if (gepackt) {
        bauer.fuegeGepacktHinzu(voll, gepackt, zeit);
        dateien += 1;
        return;
      }
    }
    try {
      bauer.fuegeHinzu(voll, ausBase64(await teile.liesDatei(voll)), zeit);
      dateien += 1;
    } catch (fehler) {
      await teile.log('Belegübergabe: ' + voll + ' übersprungen — ' + String(fehler), 'warn');
    }
  };

  for (const eintrag of await teile.liesOrdner('').catch(() => [])) {
    if (!eintrag.istOrdner && IST_LOGDATEI.test(eintrag.name)) await nimm(eintrag.name);
  }

  const geheDurch = async (pfad: string): Promise<void> => {
    const inhalt = await teile.liesOrdner(pfad).catch(() => []);
    for (const eintrag of inhalt) {
      const voll = pfad + '/' + eintrag.name;
      if (eintrag.istOrdner) {
        await geheDurch(voll);
        continue;
      }
      if (optionen.auslassen?.(voll)) continue;
      await nimm(voll);
    }
  };

  for (const wurzel of WURZELN) await geheDurch(wurzel);

  const stuecke = bauer.fertig();
  return {
    stuecke,
    dateiname: uebergabeName(zeit),
    dateien,
    bytes: bauer.bisher,
  };
}

export function baueUebergabeTeile(ueber: Partial<UebergabeTeile> = {}): UebergabeTeile {
  return {
    liesOrdner:
      ueber.liesOrdner ??
      (async (pfad) => {
        const { dateien, ordner } = await listeOrdner(pfad);
        return [
          ...dateien.map((name) => ({ name, istOrdner: false })),
          ...ordner.map((name) => ({ name, istOrdner: true })),
        ];
      }),
    liesDatei:
      ueber.liesDatei ??
      ((pfad) => liesBase64(pfad)),
    liesGepackt: ueber.liesGepackt ?? liesGepackt,
    jetzt: ueber.jetzt ?? (() => new Date()),
    log: ueber.log ?? ((text) => appendAppLog(text).catch(() => undefined)),
  };
}

/**
 * Packt und reicht das Archiv an den Browser weiter.
 *
 * ⚠️ **Die Adresse wird wieder freigegeben.** Ein `blob:`-Verweis hält den ganzen Bestand
 * fest, solange die Seite offen ist — bei einer halben Stunde Video ist das bemerkbar.
 */
export async function uebergibBelege(teile = baueUebergabeTeile()): Promise<Uebergabe> {
  const ergebnis = await sammleBelege(teile);
  const blob = new Blob(ergebnis.stuecke.map((s) => s as BlobPart), { type: 'application/zip' });
  const adresse = URL.createObjectURL(blob);
  try {
    const verweis = document.createElement('a');
    verweis.href = adresse;
    verweis.download = ergebnis.dateiname;
    verweis.click();
  } finally {
    // Erst im nächsten Durchlauf: vorher hätte der Browser den Download noch nicht begonnen.
    setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
  }
  await teile.log(
    'Belegübergabe: ' + String(ergebnis.dateien) + ' Dateien, ' + String(ergebnis.bytes) + ' Bytes.',
  );
  return ergebnis;
}

/**
 * ⚠️ **Videos bleiben draussen** aus Diagnose- und Log-ZIP. Das ZIP geht als Base64 über die
 * Brücke zum nativen Teil und liegt dabei mehrfach im Speicher; eine halbe Stunde Video sprengte
 * das. Videos kommen ohnehin über den Datei-Upload an.
 */
export const IST_VIDEO = /\.(mp4|mov|m4v)$/i;

const OHNE_TABELLEN = new Set(['ClientSettingValue']);

/**
 * Der Datenbestand als `datenbank.json` fürs Diagnose- und Log-ZIP — die alte Classic-App legte
 * ihre Datenbankdatei bei.
 *
 * ⚠️ **Ohne `ClientSettingValue`.** Dort stehen Passwörter und Serverzugang; ein ZIP liegt auf
 * dem Server, wo es viele lesen können.
 */
export async function datenbestand(store: Store): Promise<Uint8Array> {
  const alles: Record<string, unknown[]> = {};
  for (const tabelle of TABLES) {
    if (OHNE_TABELLEN.has(tabelle)) continue;
    alles[tabelle] = await store.all(tabelle).catch(() => []);
  }
  return new TextEncoder().encode(JSON.stringify(alles, null, 1));
}

export function alsBase64(stuecke: Uint8Array[]): string {
  let binaer = '';
  for (const s of stuecke) {
    for (let i = 0; i < s.length; i += 0x8000) {
      binaer += String.fromCharCode(...s.subarray(i, i + 0x8000));
    }
  }
  return btoa(binaer);
}
